// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { AvailabilityMessageType, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemStyle } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@pace/App/Components/Notify/Notify";
import ProductTooltip, { ProductTooltipStyles } from "@pace/App/Components/Product/ProductTooltip";
import { GridOffset } from "@pace/MobiusOverrides/GridContainer/GridContainer";
import Tooltip, { TooltipPresentationProps } from "@pace/MobiusOverrides/Tooltip";
import CartLineQuantity, { CartLineQuantityStyles } from "@pace/Overrides/Widgets/Cart/CartLineQuantity";
import IconAlert from "@pace/XDL/Icons/IconAlert";
import IconTrash from "@pace/XDL/Icons/IconTrash";
import { fontConfig } from "@pace/XDL/PreStyle/typography";
import { color, ellipsis, safeJsonParse, xScreen } from "@pace/XDL/Utilities";
import { getSystemColor } from "@pace/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@pace/XDL/Utilities/links";
import { mergeWithCss } from "@pace/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    cartLine: CartLineModel;
    errorMessages?: React.ReactNode[];
    hideTippy: () => void;
    cart?: Readonly<Cart>;
    qtyOrderedChangeHandler: (cartLine: CartLineModel, qtyOrdered: number) => void;
    removeCartLineClickHandler: (cartLine: CartLineModel) => void;
}

interface MiniCartLineStyles {
    cartLine?: InjectableCss;
    imageWrapper?: InjectableCss;
    cartLineImage?: LazyImageProps;
    detailsWrapper?: InjectableCss;
    partNumbersStyles?: ProductPartNumbersStyles;
    lineDescription?: LinkPresentationProps;
    productInfoWrapper?: InjectableCss;
    qtyAndPriceWrapper?: InjectableCss;
    cartLineQuantityStyles?: CartLineQuantityStyles;
    basicProductPriceStyles?: ProductPriceStyles;
    extendedNetPriceValue?: TypographyProps;
    removeCartLineLink?: LinkPresentationProps;
    errorMessagesWrapper?: InjectableCss;
    removeConfirmation?: InjectableCss;
    removeConfirmationText?: TypographyProps;
    removeConfirmationConfirm?: ButtonPresentationProps;
    removeConfirmationClose?: ButtonPresentationProps;
    // XNG-Change
    specialOrderIcon?: IconPresentationProps;
    specialOrderTooltip?: TooltipPresentationProps;
    priceContainer?: InjectableCss;
    productTooltip?: ProductTooltipStyles;
}
/* XNG-Change export it */
export const miniCartLineStyles: MiniCartLineStyles = {
    cartLine: {
        css: css`
            display: flex;
            position: relative;
            padding-bottom: 12px;
            margin-bottom: 12px;
            position: relative;
            width: 430px;
            &:not(:last-of-type) {
                border-bottom: 1px solid ${color("common.ui12")};
            }
        `,
    },
    imageWrapper: {
        css: css`
            width: 72px;
            height: 72px;
            overflow: hidden;
            margin-right: 12px;
            flex-shrink: 0;
            border-radius: 4px;
        `,
    },
    cartLineImage: {
        width: "72px",
        height: "72px",
        css: css`
            max-height: 100%;
            max-width: 100%;
            img {
                height: auto;
            }
        `,
    },
    detailsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            max-width: 345px;
            ${xScreen("lte", "md")} {
                width: 100%;
                max-width: initial;
            }
        `,
    },
    partNumbersStyles: {
        container: {
            gap: 0,
            css: css`
                margin-bottom: 8px;
                width: 100%;

                ${GridOffset} {
                    column-gap: 8px;
                    ${GridItemStyle} {
                        flex-basis: auto;
                    }
                }
            `,
        },
    },
    lineDescription: {
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
                ${ellipsis(2)}
            `,
        },
        css: css`
            margin-bottom: 4px;
            ${StyledTypography} {
                color: ${color("text.main")};
            }
        `,
    },
    qtyAndPriceWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            justify-content: flex-start;
            column-gap: 30px;
            padding-right: 20px;
            margin-top: 4px;
            position: relative;
        `,
    },
    basicProductPriceStyles: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        price: {
            priceText: {
                size: 18,
                weight: fontConfig.weight.bold,
                css: css`
                    letter-spacing: 0;
                    line-height: 22px;
                    margin-right: 4px;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    weight: fontConfig.weight.regular,
                    size: 14,
                    css: css`
                        letter-spacing: 0;
                        line-height: 22px;
                    `,
                },
            },
        },
    },
    removeCartLineLink: {
        icon: {
            iconProps: {
                src: IconTrash,
                size: 12,
                color: "secondary",
            },
        },
        css: css`
            margin-left: auto;
            position: absolute;
            right: 0;
            top: 10px;
            ${IconWrapper} {
                margin-right: 0;
            }
        `,
    },
    errorMessagesWrapper: {
        css: css`
            width: 100%;
            margin-top: 10px;
            & > div:not(:last-child) {
                margin-bottom: 15px;
            }
        `,
    },
    removeConfirmation: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            height: 0;
            margin-bottom: 0;
            transition: all 0.3s ease-in-out;
            overflow: hidden;
            opacity: 0;
            &[aria-expanded="true"] {
                height: 38px;
                border-top: 1px solid ${color("common.ui2")};
                // remove padding-bottom: 15px from parent wrapper
                margin-top: 10px;
                margin-bottom: -14px;
                opacity: 1;
            }
        `,
    },
    removeConfirmationText: {
        size: 12,
        color: "text.main",
    },
    removeConfirmationConfirm: {
        ...iconButtonCommonStyles(Check, 10, {
            typographyProps: {
                size: 13,
                color: getSystemColor("text.main", 0.6),
            },
        }),
    },
    removeConfirmationClose: {
        ...mergeWithCss(
            iconButtonCommonStyles(X, 10, {
                typographyProps: {
                    size: 13,
                    color: getSystemColor("text.main", 0.6),
                },
            }),
            {
                css: css`
                    ${IconWrapper} {
                        &,
                        & svg {
                            width: 9px;
                            height: 12px;
                        }
                    }
                `,
            },
        ),
    },
};

const styles = miniCartLineStyles;

type Props = OwnProps;

const MiniCartLineCard: FC<Props> = ({
    cartLine,
    errorMessages,
    hideTippy,
    cart,
    qtyOrderedChangeHandler,
    removeCartLineClickHandler,
}) => {
    const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
    // XNG-Change: Json string that have array of external descriptions
    const externalDoc = safeJsonParse(cartLine.properties?.misExtDesc || "[]");
    const externalDocNote = Array.isArray(externalDoc) ? externalDoc.join("\n") : "";
    return (
        <CartLineContext.Provider value={cartLine} data-test-selector={`miniCartLine_${cartLine.id}`}>
            <StyledWrapper {...styles.cartLine} data-test-selector="cartLine">
                <StyledWrapper {...styles.imageWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy}>
                        <LazyImage src={cartLine.smallImagePath} altText={cartLine.altText} {...styles.cartLineImage} />
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.detailsWrapper}>
                    <Link href={cartLine.productUri} onClick={hideTippy} {...styles.lineDescription}>
                        {cartLine.shortDescription}
                    </Link>
                    <ProductPartNumbers
                        manufacturerItem={cartLine.manufacturerItem}
                        productNumber={cartLine.erpNumber}
                        extendedStyles={styles.partNumbersStyles}
                        customerProductNumber={cartLine.customerName}
                    />
                    {externalDocNote && (
                        <ProductTooltip tooltipText={externalDoc} extendedStyles={styles.productTooltip} />
                    )}
                    <StyledWrapper {...styles.qtyAndPriceWrapper}>
                        <CartLineQuantity
                            cart={cart as Cart}
                            editable={true}
                            extendedStyles={styles.cartLineQuantityStyles}
                            onQtyOrderedChange={(inputQty: number) => {
                                qtyOrderedChangeHandler?.(cartLine, inputQty);
                            }}
                        />
                        {!cart?.cartNotPriced && (
                            /*  XNG-Changes: add Wrapper */
                            <StyledWrapper {...styles.priceContainer}>
                                <ProductPrice
                                    showLabel={false}
                                    showSavings={false}
                                    product={cartLine}
                                    extendedStyles={styles.basicProductPriceStyles}
                                />
                                {/*  XNG-Changes: add special Order icon */}
                                {cartLine.availability?.messageType === AvailabilityMessageType.OutOfStock && (
                                    <Tooltip
                                        text={siteMessage("Custom_Cart_Total_Special_Order")}
                                        triggerComponent={<Icon {...styles.specialOrderIcon} src={IconAlert} />}
                                        {...styles.specialOrderTooltip}
                                    />
                                )}
                            </StyledWrapper>
                        )}
                        {!showRemoveConfirmation && (
                            <Link
                                onClick={() => {
                                    setShowRemoveConfirmation(true);
                                }}
                                data-test-selector="cartline_removeLine"
                                {...styles.removeCartLineLink}
                            />
                        )}
                        <StyledWrapper
                            {...({ showRemoveConfirmation } as any)}
                            {...styles.removeConfirmation}
                            aria-expanded={showRemoveConfirmation}
                        >
                            <Typography {...styles.removeConfirmationText}>{translate("Are You Sure?")}</Typography>
                            <Button
                                onClick={() => {
                                    removeCartLineClickHandler(cartLine);
                                }}
                                {...styles.removeConfirmationConfirm}
                            >
                                {translate("Yes, Remove")}
                            </Button>
                            <Button
                                onClick={() => {
                                    setShowRemoveConfirmation(false);
                                }}
                                {...styles.removeConfirmationClose}
                            >
                                {translate("No")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                    {errorMessages && (
                        <StyledWrapper {...styles.errorMessagesWrapper}>
                            {errorMessages.map(message => (
                                <Notify
                                    type="danger"
                                    data-test-selector="cartline_errorMessage"
                                    key={`error_${cartLine.id}`}
                                    showClose={false}
                                    sizeVariant="small"
                                >
                                    {message}
                                </Notify>
                            ))}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </CartLineContext.Provider>
    );
};

export default MiniCartLineCard;
